import { Column, Row, Typography } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import { IPostingPlanViewEntity } from '~/domain/PermanentJobs';
import { OutlineCheckIcon } from '@tempmee/react-icons';
import { formatListingFee } from '~/components/PermanentJobsV2/utils';
import { PromoCodeSection } from './PromoCodeSection';
import { useFeatureFlag } from '~/hooks/useFeatureFlag';
import { EnforcePromoCode } from '~/constants/ff.posthog';

interface IHiringPlanCardProps {
  plan?: IPostingPlanViewEntity;
  discountedPrice?: number;
  onPromoApply?: (code: string, finalPrice: number) => void;
  onPromoRemove?: () => void;
}

export const HiringPlanCard = ({
  plan,
  discountedPrice,
  onPromoApply,
  onPromoRemove,
}: IHiringPlanCardProps) => {
  const isPromoCodeEnabled = useFeatureFlag(EnforcePromoCode);
  const originalPrice = plan?.finalPrice ? formatListingFee(plan.finalPrice) : '-';
  const price = discountedPrice !== undefined ? formatListingFee(discountedPrice) : originalPrice;

  return (
    <Column gap={4} py={6} px={16} className="border border-neutral-100 rounded-lg bg-shades-0">
      <Typography as="span" bold color={colors.neutral[600]} size="heading_3">
        Your Hiring Plan
      </Typography>

      <Column gap={4}>
        <Row gap={1}>
          <OutlineCheckIcon color={colors.secondary[500]} />
          <Column>
            <Typography as="span" color={colors.neutral[600]}>
              <Typography as="span" bold>
                Every applicant is pre-screened.
              </Typography>{' '}
              No sorting through unqualified resumes.
            </Typography>
          </Column>
        </Row>
        <Row gap={1}>
          <OutlineCheckIcon color={colors.secondary[500]} />
          <Column>
            <Typography as="span" color={colors.neutral[600]}>
              <Typography as="span" bold>
                No scheduling headaches.
              </Typography>{' '}
              Set your interview times, and professionals can apply directly to them.
            </Typography>
          </Column>
        </Row>
        <Row gap={1}>
          <OutlineCheckIcon color={colors.secondary[500]} />
          <Column>
            <Typography as="span" color={colors.neutral[600]}>
              <Typography as="span" bold>
                Tap into GoTu&apos;s active network.
              </Typography>{' '}
              Reach candidates who aren&apos;t browsing job boards.
            </Typography>
          </Column>
        </Row>
      </Column>

      {isPromoCodeEnabled && plan && onPromoApply && (
        <Column pt={4}>
          <PromoCodeSection
            basePrice={plan.basePrice}
            onApply={onPromoApply}
            onRemove={onPromoRemove}
          />
        </Column>
      )}

      <Column pt={6} pb={8} px={6} className="rounded-field bg-shades-0 shadow-elevation-4">
        <Typography as="span" size="body_large" bold color={colors.neutral[600]}>
          Job Listing Fee
        </Typography>
        <Typography as="span" size="caption" color={colors.neutral[500]}>
          {discountedPrice !== undefined && originalPrice !== '-' && (
            <Typography
              as="span"
              size="heading_2"
              color={colors.neutral[300]}
              style={{ textDecoration: 'line-through', marginRight: 4 }}
            >
              ${originalPrice}
            </Typography>
          )}
          <Typography as="span" size="heading_2" bold color={colors.neutral[600]}>
            ${price}
          </Typography>{' '}
          per month
        </Typography>
        <Typography
          as="span"
          size="body_small"
          bold
          color={colors.primary[500]}
          style={{ marginTop: 10 }}
        >
          Your job will immediately become active and notify nearby candidates.
        </Typography>
      </Column>
    </Column>
  );
};
